"use client";

import { useMemo, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useAsyncData } from "@/lib/hooks/useAsyncData";
import { useReportPageLoading } from "@/lib/hooks/useReportPageLoading";
import { ApiError } from "@/lib/api/client";
import { fetchTools, mapApiTool } from "@/lib/api/tools";
import type { Tool } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ToolCard } from "@/components/ui/ToolCard";
import { ChipRowSkeleton, ToolGridSkeleton } from "@/components/ui/Skeleton";

type CategoryChip = { name: string; slug: string };

/** Stable empties so the memoized grid doesn't see a new array each render. */
const EMPTY_TOOLS: Tool[] = [];
const EMPTY_CATEGORIES: CategoryChip[] = [];

/** Resolve `?category=` against loaded chips (slug first, then display name). */
function resolveCategoryParam(
  param: string | null,
  categories: CategoryChip[],
): string {
  if (!param) return "All";
  const bySlug = categories.find((c) => c.slug === param);
  if (bySlug) return bySlug.name;
  const byName = categories.find((c) => c.name === param);
  if (byName) return byName.name;
  return "All";
}

/**
 * Static frame of /tools (title + description). Rendered identically by the
 * route Suspense fallback and by the screen itself, so the hand-off
 * fallback → screen → data never moves anything on the page.
 *
 * The entrance animation is skipped for the skeleton frames: the fallback is a
 * separate tree, so React replaces the whole <section> when the boundary
 * resolves — a fade-up on both sides plays twice and reads as a double twitch.
 */
function ToolsShell({
  heading,
  children,
  animate = true,
}: {
  heading: string;
  children: ReactNode;
  /** Off for skeleton frames — see the note above. */
  animate?: boolean;
}) {
  const { t } = useLocale();
  return (
    <section className={cn(animate && "animate-fade-up", "px-5 pt-7")}>
      <h1 className="mb-2 text-[32px] font-medium leading-[1.05] tracking-[-1.28px]">
        {heading}
      </h1>
      <p className="mb-1.5 text-[15px] leading-[1.45] text-ink-3">{t.toolsDesc}</p>
      {children}
    </section>
  );
}

/** Route-level fallback for /tools — same frame, skeletoned body. */
export function ToolsScreenFallback() {
  const { t } = useLocale();
  return (
    <ToolsShell heading={t.toolsTitle} animate={false}>
      <p className="mb-5 text-[12.5px] font-semibold text-transparent" aria-hidden="true">
        &nbsp;
      </p>
      <ChipRowSkeleton />
      <ToolGridSkeleton />
    </ToolsShell>
  );
}

/** /tools — category chips + tool grid from GET /v1/tool. */
export function ToolsScreen() {
  const { t, locale, hydrated: localeHydrated } = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const categoryParam = searchParams.get("category");

  // Wait for locale from localStorage so Accept-Language matches the UI.
  const requestKey = localeHydrated ? locale : null;
  const { status, data, error, reload } = useAsyncData(requestKey, async () => {
    const items = await fetchTools(locale);
    const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
    const categories: CategoryChip[] = [];
    for (const item of sorted) {
      if (!categories.some((c) => c.slug === item.category.slug)) {
        categories.push({ name: item.category.name, slug: item.category.slug });
      }
    }
    return { tools: sorted.map(mapApiTool), categories };
  });

  const tools = data?.tools ?? EMPTY_TOOLS;
  const categories = data?.categories ?? EMPTY_CATEGORIES;
  const loading = status === "loading" || status === "idle";
  const errorMessage =
    status === "error"
      ? error instanceof ApiError
        ? error.message
        : t.authErrorFallback
      : null;

  // The chip selection lives in the URL — deriving it keeps one source of truth
  // (and no effect to re-sync state after the list loads).
  const cat = resolveCategoryParam(categoryParam, categories);

  useReportPageLoading(!localeHydrated || loading);

  const selectCategory = (next: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (next === "All") {
      params.delete("category");
    } else {
      const chip = categories.find((c) => c.name === next);
      params.set("category", chip?.slug ?? next);
    }
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  const visible = useMemo(
    () => (cat === "All" ? tools : tools.filter((x) => x.category === cat)),
    [cat, tools],
  );

  const heading = cat === "All" ? t.toolsTitle : `${t.toolsTitle} · ${cat}`;
  const chips = ["All", ...categories.map((c) => c.name)];

  if (errorMessage && !loading) {
    return (
      <ToolsShell heading={t.toolsTitle}>
        <p className="mb-5 mt-3.5 text-[15px] leading-[1.45] text-ink-3">{errorMessage}</p>
        <button
          type="button"
          onClick={reload}
          className="rounded-full border border-[rgba(26,24,22,0.16)] bg-surface px-4 py-[9px] text-[13px] font-medium text-ink transition hover:bg-[rgba(26,24,22,0.04)]"
        >
          {t.retry}
        </button>
      </ToolsShell>
    );
  }

  if (loading) {
    return (
      <ToolsShell heading={t.toolsTitle} animate={false}>
        {/* Transparent copy of the count line keeps its box reserved. */}
        <p className="mb-5 text-[12.5px] font-semibold text-transparent" aria-hidden="true">
          &nbsp;
        </p>
        <ChipRowSkeleton />
        <ToolGridSkeleton />
      </ToolsShell>
    );
  }

  return (
    <ToolsShell heading={heading}>
      <p className="mb-5 text-[12.5px] font-semibold text-muted">
        {visible.length} {t.toolsCountLabel}
      </p>

      <div className="no-scrollbar -mx-0.5 mb-4 flex gap-2 overflow-x-auto px-0.5 pb-4 pt-0.5">
        {chips.map((c) => {
          const active = cat === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => selectCategory(c)}
              className={cn(
                "flex-none whitespace-nowrap rounded-full border px-4 py-[9px] text-[13px] font-medium transition",
                active
                  ? "border-accent bg-accent text-white"
                  : "border-transparent bg-chip text-ink",
              )}
            >
              {c === "All" ? t.all : c}
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 pb-2 [grid-template-columns:repeat(auto-fill,minmax(270px,1fr))]">
        {visible.map((tool) => (
          <ToolCard key={tool.slug} tool={tool} />
        ))}
      </div>
    </ToolsShell>
  );
}
